import { computed, ref, watch } from 'vue'

import { customersService } from '@/api/services/customers'
import type { ApiCustomerAddress, AddressPayload } from '@/api/types/address'
import { useRequest } from '@/composables/useRequest'
import { useUserStore } from '@/stores/user'

/**
 * Адрес доставки. Хранит его бэкенд (`/customers/*Address`), здесь он живёт
 * в том виде, в каком его показывают экраны: строка адреса отдельно, детали
 * входа отдельно.
 *
 * `id` — это `addressId` из справочника: он же уходит в заказ, поэтому заказ
 * ссылается на ту самую запись, которую видит оператор в CRM.
 */
export interface SavedAddress {
  id: number
  /** Строка адреса от геокодера — её показываем в списке (`line` у бэкенда) */
  name: string
  longitude: number
  latitude: number
  region: number
  /** Как клиент назвал адрес: «Дом», «Работа» (`name` у бэкенда) */
  label?: string
  /**
   * Номер дома. Приезжает из разбора адреса Яндексом, в форме его правят
   * как «Кв./Офис»: геокодер часто знает только улицу, а курьеру нужен дом.
   */
  house?: string
  entrance?: string
  floor?: string
  flat?: string
  comment?: string
}

/** Черновик с формы — всё то же самое, только без идентификатора. */
export type AddressDraft = Omit<SavedAddress, 'id'>

/**
 * Тот же дом клиент никогда не поймает в ту же точку: центр карты отдаёт
 * координаты с точностью до сантиметров. Считаем адрес прежним, если он
 * в этом радиусе — с запасом на промах пальцем по карте.
 */
const SAME_ADDRESS_METERS = 50

const METERS_PER_DEGREE = 111_320

/** Плоская аппроксимация: на десятках метров разница с гаверсинусом ничтожна. */
function distanceMeters(a: SavedAddress, longitude: number, latitude: number): number {
  const dLat = (a.latitude - latitude) * METERS_PER_DEGREE
  const dLon = (a.longitude - longitude) * METERS_PER_DEGREE * Math.cos((latitude * Math.PI) / 180)
  return Math.hypot(dLat, dLon)
}

/** Запись бэкенда → адрес в том виде, в каком его знают экраны. */
function toSaved(item: ApiCustomerAddress): SavedAddress {
  return {
    id: item.addressId,
    /* Строка адреса: у записей из старых версий она лежит в названии */
    name: (item.line?.trim() || item.name?.trim()) ?? '',
    longitude: Number(item.lon ?? 0),
    latitude: Number(item.lat ?? 0),
    region: item.regionId,
    ...(item.name ? { label: item.name } : {}),
    ...(item.house ? { house: item.house } : {}),
    ...(item.entrance ? { entrance: item.entrance } : {}),
    ...(item.floor ? { floor: item.floor } : {}),
    ...(item.flat ? { flat: item.flat } : {}),
    ...(item.comment ? { comment: item.comment } : {}),
  }
}

/**
 * Адрес → тело запроса. `name` у бэкенда обязателен, а название клиент
 * ставит не всегда — тогда подставляем саму строку адреса: пустое имя
 * бэкенд не принимает, а в CRM так понятнее, чем «Адрес».
 */
const NAME_LIMIT = 100

function toPayload(address: AddressDraft): AddressPayload {
  return {
    name: (address.label || address.name || '—').slice(0, NAME_LIMIT),
    regionId: address.region,
    line: address.name,
    house: address.house ?? '',
    entrance: address.entrance ?? '',
    floor: address.floor ?? '',
    flat: address.flat ?? '',
    /* Домофона в форме нет — поле есть у бэкенда, но в макете его не рисуют */
    doorphone: '',
    lon: String(address.longitude),
    lat: String(address.latitude),
    comment: address.comment ?? '',
  }
}

/**
 * Годится ли запись для показа. В старых версиях адрес сохраняли в `name`,
 * а `line` оставался пустым — такие записи полноценные, с координатами, и
 * прятать их нельзя: клиент их сохранял и ждёт увидеть. Без адреса совсем
 * показывать действительно нечего.
 */
function isUsable(item: ApiCustomerAddress): boolean {
  return Boolean(item.line?.trim() || item.name?.trim())
}

/**
 * Справочник адресов клиента. Все правки идут на бэкенд, список после них
 * обновляется ответом (создание отдаёт его целиком) или перечитывается.
 */
export function useAddresses() {
  const userStore = useUserStore()

  const list = ref<SavedAddress[]>([])

  const createRequest = useRequest(customersService.createAddress)
  const updateRequest = useRequest(customersService.updateAddress)
  const deleteRequest = useRequest(customersService.deleteAddress)

  const addresses = computed(() => list.value)

  const isSaving = computed(
    () => createRequest.isLoading.value || updateRequest.isLoading.value || deleteRequest.isLoading.value,
  )

  function setList(items: ApiCustomerAddress[] | null | undefined): void {
    list.value = (items ?? []).filter(isUsable).map(toSaved)
  }

  /*
   * Карточка клиента приходит на старте и после входа — отдельного запроса
   * за адресами не делаем, просто следим за её справочником.
   */
  watch(() => userStore.addressList, setList, { immediate: true })

  /** Перечитать справочник — это перечитать карточку клиента целиком. */
  async function loadAddresses(): Promise<void> {
    if (!userStore.userId) {
      list.value = []
      return
    }

    await userStore.getUserById(userStore.userId)
  }

  /** Ближайший сохранённый адрес к точке — или ничего, если все дальше радиуса. */
  function findNearest(longitude: number, latitude: number): SavedAddress | undefined {
    return list.value
      .map((address) => ({ address, distance: distanceMeters(address, longitude, latitude) }))
      .filter((item) => item.distance <= SAME_ADDRESS_METERS)
      .sort((a, b) => a.distance - b.distance)[0]?.address
  }

  /**
   * Новый адрес. Дубль не создаём: тот же дом уже лежит в справочнике, и
   * второй записью он только засорит и список, и CRM.
   */
  async function addAddress(draft: AddressDraft): Promise<SavedAddress | null> {
    const same = findNearest(draft.longitude, draft.latitude)
    if (same) return same

    if (!userStore.userId) return null

    await createRequest.execute(userStore.userId, toPayload(draft))
    if (createRequest.error.value) return null

    /*
     * Ответ создания — весь справочник, но в нём лежат и удалённые записи,
     * и старые, без строки адреса. Отфильтрованный список знает только
     * карточка клиента, поэтому за новым адресом идём к ней.
     */
    await loadAddresses()
    return findNearest(draft.longitude, draft.latitude) ?? null
  }

  /** Детали адреса заполняются на следующем экране, уже после подтверждения точки. */
  async function updateAddress(id: number, patch: Partial<SavedAddress>): Promise<boolean> {
    const current = list.value.find((address) => address.id === id)
    if (!current) return false

    const next = { ...current, ...patch, id }
    const result = await updateRequest.execute({ ...toPayload(next), addressId: id })
    if (result === null && updateRequest.error.value) return false

    /* Ответ пустой — состояние правим у себя, лишний запрос за списком не нужен */
    list.value = list.value.map((address) => (address.id === id ? next : address))
    return true
  }

  async function removeAddress(id: number): Promise<boolean> {
    const result = await deleteRequest.execute(id)
    if (result === null && deleteRequest.error.value) return false

    list.value = list.value.filter((address) => address.id !== id)
    return true
  }

  return {
    addresses,
    isSaving,
    setList,
    loadAddresses,
    findNearest,
    addAddress,
    updateAddress,
    removeAddress,
  }
}
